import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from "@angular/core";

import { Observable, Subscription } from "rxjs";

import { Pending } from "../../../shared-library/decorators/pending.decorator";
import { PaginationOptionsModel } from "../../../shared-library/form-controls/pagination/pagination-options.model";
import { DisposeMixin, EmptyClassInstance } from "../../../shared-library/mixins/dispose.mixin";
import { LockMixin } from "../../../shared-library/mixins/lock.mixin";
import { BrowserWindowService } from "../../../shared-library/services/environment/browser-window.service";

import { BaseSearchTabConfigModel } from "../../models/base-search-tab-config.model";
import { PaginationOptionsFactoryService } from "../../services/pagination-options-factory.service";

import { MarketplaceSearchForm } from "./forms/marketplace-search.form";
import { MarketplaceSearchParametersModel } from "./models/marketplace-search-parameters.model";
import { MarketplaceSearchResultsModel } from "./models/marketplace-search-results.model";
import { SearchTabConfigModel } from "./models/search-tab-config.model";
import { MarketplaceSearchParametersFactoryService } from "./services/marketplace-search-parameters-factory.service";
import { MarketplaceSearchService } from "./services/marketplace-search.service";
import {SearchTabFilterModel} from "./models/search-tab-filter.model";
import {BaseSearchParametersModel} from "../../models/base-search-parameters.model";
import {
    QueryStringSerializationService
} from "../../../shared-library/services/environment/query-string-serialization.service";

declare function externalUpdateActiveSearchTabFn(searchTabAlias: string): any;

@Component({
    selector: "pcg-ng-marketplace-search",
    template: require('./marketplace-search.component.html')
})
export class MarketplaceSearchComponent extends DisposeMixin(LockMixin(EmptyClassInstance)) implements OnInit, AfterViewInit, OnDestroy {
    @Input()
    public searchTabConfig: SearchTabConfigModel;

    @Input()
    public onTabActivated: Observable<BaseSearchTabConfigModel>;

    @Input()
    public searchPageConfig: Array<BaseSearchTabConfigModel>;

    @Output()
    public changeActiveTabDelegate = new EventEmitter<BaseSearchTabConfigModel>();

    public searchForm: MarketplaceSearchForm;
    public searchResult: MarketplaceSearchResultsModel;
    public tabFilter: Array<SearchTabFilterModel> = new Array<SearchTabFilterModel>();

    public paginationOptions: PaginationOptionsModel;

    @Pending
    private _searchInProgressSubscription: Subscription;

    private _formChangedSubscription: Subscription;
    private _formSubmittedSubscription: Subscription;
    private _tabActivatedSubscription: Subscription;

    public constructor(
        private _browserWindowService: BrowserWindowService,
        private _paginationOptionsFactory: PaginationOptionsFactoryService,
        private _searchParametersFactory: MarketplaceSearchParametersFactoryService,
        private _searchService: MarketplaceSearchService,
        private _serializationService: QueryStringSerializationService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.paginationOptions = this._paginationOptionsFactory.createDefaultPaginationOptions();

        const initialSearchParams = this._searchParametersFactory.createFromQueryString(this._browserWindowService.getQueryString(), this.searchTabConfig);
        const emptySearchParams = this._searchParametersFactory.createFromQueryString("", this.searchTabConfig);

        this.searchForm = MarketplaceSearchForm.createForm(initialSearchParams, emptySearchParams);

        this._formChangedSubscription = this.searchForm.onFormChanged.subscribe(
            (searchParams: MarketplaceSearchParametersModel) => {
                return this.invokeSearch(searchParams);
            }
        );

        this._formSubmittedSubscription = this.searchForm.onFormSubmitted.subscribe(
            (searchParams: MarketplaceSearchParametersModel) => {
                return this.invokeSearch(searchParams);
            }
        );

        this._tabActivatedSubscription = this.onTabActivated.subscribe((tab) => this.onTabActivatedHandler(tab));
        this.buildTabFilter();
        (window as any).getSearchFilters = this.getSearchFilters.bind(this);
    }

    public ngAfterViewInit(): void {
        this.searchForm.submit();
    }

    public ngOnDestroy(): void {
        this.dispose();
        this.searchForm.dispose();
    }

    private onTabActivatedHandler(activatedTab: BaseSearchTabConfigModel): void {
        if (activatedTab.tabAlias === this.searchTabConfig.tabAlias) {
            this._browserWindowService.setQueryString(this._searchParametersFactory.serialiseToQueryString(this.searchForm.formValue));
            externalUpdateActiveSearchTabFn(activatedTab.tabAlias);
            this.buildTabFilter();
        } else {
            const searchString = this._browserWindowService.getQueryString();
            const searchParams = this._serializationService.deserialize<BaseSearchParametersModel>(searchString);
            if (searchParams && searchParams.searchTab !== activatedTab.tabAlias) {
                externalUpdateActiveSearchTabFn(activatedTab.tabAlias);
            }
        }
    }

    private invokeSearch(searchParams: MarketplaceSearchParametersModel): void {
        if (this.searchTabConfig.isActive) {
            this._browserWindowService.setQueryString(this._searchParametersFactory.serialiseToQueryString(searchParams));
        }

        this._searchInProgressSubscription = this._searchService.getSearchResults(searchParams).subscribe(
            (result: MarketplaceSearchResultsModel) => {
                this.searchResult = result;
                window.scrollTo(0, 0);
            },
            () => {
            }
        );
    }

    private onActiveTabChangedDelegate(activeTab: BaseSearchTabConfigModel): void {
        this.changeActiveTabDelegate.emit(activeTab);
    }

    private buildTabFilter(): void {
        this.tabFilter = [];

        this.searchPageConfig.forEach(tab => {
            const checkboxControl = new SearchTabFilterModel();
            checkboxControl.tab = tab;
            checkboxControl.isSelected = tab.tabAlias === this.searchTabConfig.tabAlias;

            this.tabFilter.push(checkboxControl);
        });
    }
    public getSearchFilters(): string {
        const searchTabCaption = this.searchTabConfig?.tabCaption ? this.searchTabConfig.tabCaption : "";
        const categoriesId = this.searchForm?.categories.value;
        let filter = this.searchTabConfig.searchFilters.find(item => item.alias === "categories");
        const categoryNames: Array<string> = [];
        if (filter && categoriesId) {
            filter.attributes?.forEach(item => {
                if (categoriesId.includes(item.id)) {
                    categoryNames.push(item.name);
                }
                item.nestedAttributes?.forEach(nestedItem => {
                    if (categoriesId.includes(nestedItem.id)) {
                        categoryNames.push(nestedItem.name);
                    }
                });
            });
        }
        const categories = categoryNames.join(", ");
        const areasId = this.searchForm?.areas.value;
        filter = this.searchTabConfig.searchFilters.find(item => item.alias === "areas");
        const areasNames: Array<string> = [];
        if (filter && areasId) {
            filter.attributes?.forEach(item => {
                if (areasId.includes(item.id)) {
                    areasNames.push(item.name);
                }
                item.nestedAttributes?.forEach(nestedItem => {
                    if (areasId.includes(nestedItem.id)) {
                        areasNames.push(nestedItem.name);
                    }
                });
            });
        }
        const areas = areasNames.join(", ");

        const result = [searchTabCaption, areas, categories].filter(str => str !== "").join(", ");
        return result;
    }
}
